import { authService } from "@/services/auth-service";
import { useAuthStore } from "@/store/auth-store";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: false, // No longer need cookies for JWT auth
});

// Request queue for handling token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Wait for Clerk to fully load and return a session token.
 * window.Clerk exists early but .session may not be populated yet
 * when the first API calls fire — this causes 401s.
 */
const getClerkToken = async (): Promise<string | null> => {
  if (typeof window === "undefined" || !window.Clerk) return null;

  const clerk = window.Clerk;

  // If session is already available, grab the token immediately
  if (clerk.session) {
    return (await clerk.session.getToken()) || null;
  }

  // Clerk exists but session isn't ready yet — wait for it
  // Clerk.load() is idempotent; if already loading it returns the same promise
  try {
    if (typeof clerk.load === "function") {
      await clerk.load();
    }
    // Re-read from window.Clerk after load() since session is now set
    const loadedClerk = window.Clerk;
    if (loadedClerk?.session) {
      return (await loadedClerk.session.getToken()) || null;
    }
  } catch {
    // Clerk load failed, fall through
  }

  // Last resort: poll briefly for session to appear (max ~2s)
  return new Promise((resolve) => {
    let elapsed = 0;
    const interval = 100;
    const maxWait = 2000;
    const timer = setInterval(() => {
      elapsed += interval;
      if (window.Clerk?.session) {
        clearInterval(timer);
        window.Clerk.session
          .getToken()
          .then((t: string | null) => resolve(t || null))
          .catch(() => resolve(null));
      } else if (elapsed >= maxWait) {
        clearInterval(timer);
        resolve(null);
      }
    }, interval);
  });
};

// Add request interceptor to include authentication headers
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token injection for auth endpoints
    if (
      config.url?.includes("/auth/login") ||
      config.url?.includes("/auth/refresh") ||
      config.url?.includes("/forgot_pass") ||
      config.url?.includes("/create_user") || // for portal
      config.url?.includes("/fetch_portal_opportunity") || // for portal
      config.url?.includes("/create_company_sandbox")
    ) {
      return config;
    }

    try {
      // First try to get Clerk session token
      const clerkToken = await getClerkToken();
      if (clerkToken) {
        config.headers.Authorization = `Bearer ${clerkToken}`;
        return config;
      }

      // Fallback to JWT token for backward compatibility
      const token = await authService.getValidAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // If we can't get a valid token, continue without it
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If server returns 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip retry for auth endpoints
      if (
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/logout")
      ) {
        return Promise.reject(error);
      }

      // For Clerk authentication, don't try to refresh JWT tokens
      // Clerk handles its own authentication and token refresh
      if (typeof window !== "undefined" && window.Clerk?.session) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await authService.refreshAccessToken();
        const newToken = authService.getAccessToken();

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // If refresh fails, logout the user
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Enrich error with user-friendly message from backend
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { message?: string; error?: string };
      (error as any).userMessage = data.message || data.error || "";
    }

    return Promise.reject(error);
  }
);
